(() => {
  if (window.DriplockedEditorial) return;
  const controllers = new Map();
  const motion = () => matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  const storage = {
    get(key) { try { return sessionStorage.getItem(key); } catch { return null; } },
    set(key, value) { try { sessionStorage.setItem(key, value); } catch { /* Storage is optional. */ } }
  };
  const focusable = root => [...root.querySelectorAll('a[href],button:not(:disabled),input:not(:disabled),select:not(:disabled),summary,[tabindex="0"]')].filter(el => el.getClientRects().length && !el.closest('[inert]'));
  let activeMenu;
  function closeMenu(restore = true) { activeMenu?.close(restore); }
  window.DriplockedEditorial = { closeMenu };

  function header(root) {
    const abort = new AbortController();
    const on = (el, type, fn, opts = {}) => el.addEventListener(type, fn, { ...opts, signal: abort.signal });
    const menu = root.querySelector('[data-dl-menu]');
    const summary = menu.querySelector(':scope > summary');
    const panel = menu.querySelector('.dl-menu-panel');
    const wrapper = root.closest('.shopify-section') || root;
    let locked = false, previousOverflow, previousPadding, previousFocus, inertElements = [];
    const unlock = restore => {
      if (!locked) return;
      locked = false;
      document.documentElement.style.overflow = previousOverflow;
      document.body.style.paddingRight = previousPadding;
      inertElements.forEach(([el, was]) => { if (el.isConnected) el.inert = was; });
      inertElements = [];
      root.removeAttribute('role'); root.removeAttribute('aria-modal'); root.removeAttribute('aria-label');
      summary.setAttribute('aria-expanded', 'false');
      if (restore && previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
      if (activeMenu?.root === root) activeMenu = null;
    };
    let menuAnimation, closing = false;
    const animateMenu = (opening, restore = true) => {
      const from = menu.open ? getComputedStyle(panel).clipPath : 'inset(0 0 100% 0)';
      menuAnimation?.cancel();
      closing = !opening;
      if (opening) { menu.open = true; sync(); }
      if (!menu.open) return;
      const finish = () => {
        if (!opening) { menu.open = false; unlock(restore); }
        closing = false;
        menuAnimation = null;
      };
      if (motion() === 'auto' || !restore) { finish(); return; }
      menuAnimation = panel.animate([
        { clipPath: from },
        { clipPath: opening ? 'inset(0 0 0% 0)' : 'inset(0 0 100% 0)' }
      ], { duration: opening ? 680 : 480, easing: 'cubic-bezier(.22, 1, .36, 1)' });
      menuAnimation.onfinish = finish;
    };
    const close = (restore = true) => animateMenu(false, restore);
    const sync = () => {
      summary.setAttribute('aria-expanded', String(menu.open));
      if (!menu.open) { unlock(true); return; }
      if (locked) return;
      const otherOverlay = document.querySelector('#cart-drawer.open,.email-popup__overlay.active,.fullscreen-gallery.active');
      if (otherOverlay) { menu.open = false; return; }
      closeMenu(false);
      locked = true; previousFocus = summary;
      previousOverflow = document.documentElement.style.overflow;
      previousPadding = document.body.style.paddingRight;
      const scrollbar = Math.max(0, innerWidth - document.documentElement.clientWidth);
      document.documentElement.style.overflow = 'hidden';
      if (scrollbar) document.body.style.paddingRight = `${scrollbar}px`;
      let current = wrapper;
      while (current.parentElement && current !== document.body) {
        [...current.parentElement.children].forEach(el => {
          if (el === current || ['SCRIPT','STYLE','LINK'].includes(el.tagName)) return;
          inertElements.push([el, el.inert]); el.inert = true;
        });
        current = current.parentElement;
      }
      root.setAttribute('role','dialog'); root.setAttribute('aria-modal','true'); root.setAttribute('aria-label',summary.textContent.trim());
      activeMenu = { root, close };
    };
    on(menu, 'toggle', sync);
    on(summary, 'click', e => { e.preventDefault(); animateMenu(!menu.open || closing); });
    on(root.querySelector('[data-dl-veil]'), 'click', () => close());
    on(root,'click',e => { if (e.target.closest('a')) close(false); });
    on(document,'keydown',e => {
      if (!menu.open) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); }
      if (e.key === 'Tab') {
        const nodes = focusable(root), first = nodes[0], last = nodes.at(-1);
        if (e.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) { e.preventDefault(); last?.focus(); }
        else if (!e.shiftKey && (document.activeElement === last || !root.contains(document.activeElement))) { e.preventDefault(); first?.focus(); }
      }
    });
    on(document,'cart:open',()=>close(false));
    on(document,'dl:overlay-open',()=>close(false));
    const size = new ResizeObserver(() => document.body.style.setProperty('--dl-header-height',`${wrapper.getBoundingClientRect().height}px`));
    size.observe(wrapper);
    sync();
    const cleanupClock = clock(root.querySelector('[data-dl-clock]'));
    return () => { close(false); abort.abort(); size.disconnect(); cleanupClock(); };
  }

  function clock(el) {
    if (!el) return () => {};
    const target = el.querySelector('[data-dl-time]');
    let zone = el.dataset.timezone || 'Asia/Kolkata';
    try { new Intl.DateTimeFormat('en',{timeZone:zone}).format(); } catch { zone = 'Asia/Kolkata'; }
    const formatter = new Intl.DateTimeFormat('en-US',{timeZone:zone,month:'short',day:'numeric',hour:'numeric',minute:'2-digit',hour12:el.dataset.format !== '24'});
    const tick = () => {
      const now = new Date(), parts = Object.fromEntries(formatter.formatToParts(now).map(p=>[p.type,p.value]));
      target.textContent = `${parts.month.toUpperCase()}. ${parts.day} – ${parts.hour}:${parts.minute}${parts.dayPeriod || ''}`;
      target.dateTime = now.toISOString();
    };
    tick(); const timer = setInterval(tick,15000);
    const abort = new AbortController();
    let weatherTimer;
    const temperature = el.querySelector('[data-dl-temperature]');
    const weather = async () => {
      if (!el.dataset.weather || document.hidden) return;
      try {
        const url = new URL(el.dataset.weather,location.origin);
        if (url.protocol !== 'https:') return;
        const cached = storage.get(`dl-weather:${url}`);
        const data = cached ? JSON.parse(cached) : null;
        if (data && Date.now()-data.time < 1800000) { temperature.textContent = ` ${Math.round(data.temperature)}°C`; return; }
        const response = await fetch(url,{signal:AbortSignal.any([abort.signal,AbortSignal.timeout(5000)]),credentials:'omit',cache:'no-cache'});
        if (!response.ok) throw new Error('Weather request failed');
        const result = await response.json();
        const value = result.temperature_c;
        if (typeof value !== 'number' || !Number.isFinite(value) || value < -90 || value > 70) throw new Error('Invalid weather response');
        temperature.textContent = ` ${Math.round(value)}°C`;
        storage.set(`dl-weather:${url}`,JSON.stringify({time:Date.now(),temperature:value}));
      } catch (error) { if (error.name !== 'AbortError') temperature.textContent = ''; }
    };
    if (el.dataset.weather) { weather(); weatherTimer=setInterval(weather,1800000); }
    return () => { clearInterval(timer); clearInterval(weatherTimer); abort.abort(); };
  }

  function product(root) {
    root.querySelectorAll('[data-dl-options]').forEach(el => { el.hidden = false; });
    const abort = new AbortController();
    const on = (el,type,fn,opts={}) => el.addEventListener(type,fn,{...opts,signal:abort.signal});
    const info = root.querySelector('[data-dl-info]');
    const gallery = root.querySelector('[data-dl-gallery]');
    const controls = root.querySelector('[data-dl-gallery-controls]');
    const slides = [...gallery.querySelectorAll('[data-media-id]')];
    let frame, selectionRequest;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame=requestAnimationFrame(() => {
        if (!root.isConnected) return;
        const headerHeight=parseFloat(getComputedStyle(document.body).getPropertyValue('--dl-header-height')) || 76;
        const available=innerHeight-headerHeight-32;
        const tall=info.scrollHeight>available;
        info.toggleAttribute('data-tall',tall);
        const offset=tall ? 24 : Math.max(24,(available-info.scrollHeight)/2);
        info.style.setProperty('--dl-info-offset',`${offset}px`);
        info.style.setProperty('--dl-sticky-top',`${headerHeight+offset}px`);
      });
    };
    const observer=new ResizeObserver(measure);observer.observe(info);observer.observe(document.body);on(window,'resize',measure);measure();
    let galleryIndex=0;
    const position=() => {
      if (!controls || innerWidth>=1024) return;
      galleryIndex=Math.round(gallery.scrollLeft/Math.max(1,gallery.clientWidth));
      controls.querySelector('[data-gallery-position]').textContent=`${galleryIndex+1} / ${slides.length}`;
      controls.querySelector('[data-gallery-prev]').disabled=galleryIndex===0;
      controls.querySelector('[data-gallery-next]').disabled=galleryIndex>=slides.length-1;
    };
    const go=index => gallery.scrollTo({left:Math.max(0,Math.min(slides.length-1,index))*gallery.clientWidth,behavior:motion()});
    if (controls) {
      controls.hidden=false;
      on(controls,'click',e=>{if(e.target.closest('[data-gallery-prev]'))go(galleryIndex-1);if(e.target.closest('[data-gallery-next]'))go(galleryIndex+1);});
      on(gallery,'scroll',position,{passive:true});on(window,'resize',position);position();
    }
    const reveal=id => {
      const slide=slides.find(el=>el.dataset.mediaId===String(id));
      if (!slide) return;
      if(innerWidth<1024) gallery.scrollTo({left:slide.offsetLeft-gallery.offsetLeft,behavior:'auto'});
      else {
        const rect=slide.getBoundingClientRect();
        if(rect.bottom<100 || rect.top>innerHeight*.7) slide.scrollIntoView({block:'start',behavior:motion()});
      }
    };
    if (new URL(location.href).searchParams.has('variant') && root.dataset.initialMedia) requestAnimationFrame(()=>reveal(root.dataset.initialMedia));
    const animateDisclosure=(details,opening) => {
      const summary=details.querySelector(':scope > summary');
      const start=details.getBoundingClientRect().height;
      details.getAnimations().forEach(a=>a.cancel());
      if(opening)details.open=true;
      const end=opening ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height;
      summary.setAttribute('aria-expanded',String(opening));
      if (motion()==='auto') { details.open=opening;measure();return; }
      const animation=details.animate([{height:`${start}px`,overflow:'hidden'},{height:`${end}px`,overflow:'hidden'}],{duration:220,easing:'ease-out'});
      animation.onfinish=()=>{details.open=opening;measure();};
      animation.oncancel=()=>measure();
      measure();
    };
    root.querySelectorAll('[data-dl-disclosure]').forEach(d=>d.querySelector('summary').setAttribute('aria-expanded',String(d.open)));
    on(root,'click',e=>{
      const summary=e.target.closest('[data-dl-disclosure] > summary');if(!summary)return;
      e.preventDefault();const d=summary.parentElement,opening=!d.open;
      root.querySelectorAll('[data-dl-disclosure][open]').forEach(other=>{if(other!==d)animateDisclosure(other,false);});
      animateDisclosure(d,opening);
    });
    on(root,'change',async e=>{
      const radio=e.target.closest('[data-option-value-id]');if(!radio)return;
      if(radio.dataset.productUrl && radio.dataset.productUrl!==root.dataset.productUrl) { location.assign(radio.dataset.productUrl);return; }
      selectionRequest?.abort();selectionRequest=new AbortController();
      const request=selectionRequest;
      const region=root.querySelector('[data-dl-variant-region]');
      const fields=[...root.querySelectorAll('[data-option-value-id]:checked')].map(input=>input.dataset.optionValueId);
      const url=new URL(root.dataset.productUrl,location.origin);url.searchParams.set('option_values',fields.join(','));url.searchParams.set('section_id',root.dataset.sectionId);
      const oldForm=region.querySelector('form');
      const preserved=[...new FormData(oldForm).entries()].filter(([key])=>key==='quantity'||key==='selling_plan'||key.startsWith('properties['));
      region.setAttribute('aria-busy','true');
      region.querySelector('[name=id]').disabled=true;
      region.querySelector('[data-dl-purchase]').disabled=true;
      try {
        const response=await fetch(url,{signal:request.signal,headers:{'X-Requested-With':'XMLHttpRequest'}});
        if(!response.ok)throw new Error('Variant section failed');
        const documentFragment=new DOMParser().parseFromString(await response.text(),'text/html');
        const replacement=documentFragment.querySelector('[data-dl-variant-region]');
        if(!replacement)throw new Error('Variant content missing');
        if(request!==selectionRequest || !root.isConnected)return;
        const selected=replacement.querySelector('[data-selected-variant]');
        region.replaceWith(replacement);
        replacement.querySelectorAll('[data-dl-options]').forEach(el => { el.hidden = false; });
        const newForm=replacement.querySelector('form');
        for(const [key,value] of preserved){const field=[...newForm.elements].find(f=>f.name===key);if(field){if(field.tagName!=='SELECT'||[...field.options].some(o=>o.value===value))field.value=value;}else if(key.startsWith('properties[')){const hidden=document.createElement('input');hidden.type='hidden';hidden.name=key;hidden.value=value;newForm.append(hidden);}}
        const newUrl=new URL(location.href);newUrl.searchParams.delete('option_values');
        if(selected.dataset.selectedVariant)newUrl.searchParams.set('variant',selected.dataset.selectedVariant);else {newUrl.searchParams.delete('variant');newUrl.searchParams.set('option_values',fields.join(','));}
        history.replaceState(history.state,'',newUrl);
        replacement.querySelector(`#${CSS.escape(radio.id)}`)?.focus({preventScroll:true});
        reveal(selected.dataset.selectedMedia);measure();
        document.dispatchEvent(new CustomEvent('variant:change',{detail:{variantId:selected.dataset.selectedVariant,sectionId:root.dataset.sectionId}}));
      } catch(error) {
        if(error.name==='AbortError')return;
        const message=region.querySelector('[data-selection-message]');message.textContent=root.dataset.variantError;message.hidden=false;
      } finally { if(request===selectionRequest && root.isConnected)root.querySelector('[data-dl-variant-region]')?.removeAttribute('aria-busy'); }
    });
    return()=>{abort.abort();selectionRequest?.abort();observer.disconnect();cancelAnimationFrame(frame);};
  }

  function collection(root) {
    const abort=new AbortController();let loading=false,failed=false;
    const controls=root.querySelector('[data-dl-view-controls]');controls.hidden=false;
    const setView=view=>{
      root.dataset.view=view==='index'?'index':'grid';
      controls.querySelectorAll('[data-view-mode]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.viewMode===root.dataset.view)));
    };
    setView(storage.get('dl-collection-view'));
    controls.addEventListener('click',e=>{const button=e.target.closest('[data-view-mode]');if(button){setView(button.dataset.viewMode);storage.set('dl-collection-view',root.dataset.view);}},{signal:abort.signal});
    const status=root.querySelector('[data-dl-load-status]');
    const load=async()=>{
      const next=root.querySelector('[data-dl-next]');if(!next||loading||failed)return;
      loading=true;status.hidden=false;status.textContent=root.dataset.loading;
      try {
        const url=new URL(next.href);url.searchParams.set('section_id',root.id.replace('DriplockedCollection-',''));
        const response=await fetch(url,{signal:abort.signal});if(!response.ok)throw new Error('Collection load failed');
        const doc=new DOMParser().parseFromString(await response.text(),'text/html');const results=doc.querySelector('[data-dl-results]'),pagination=doc.querySelector('[data-dl-pagination]');
        if(!results||!pagination)throw new Error('Collection content missing');
        const target=root.querySelector('[data-dl-results]');const existing=new Set([...target.children].map(c=>c.dataset.productId));
        [...results.children].forEach(card=>{if(card.dataset.productId&&!existing.has(card.dataset.productId)){target.append(card);existing.add(card.dataset.productId);}});
        root.querySelector('[data-dl-pagination]').replaceWith(pagination);observe();status.hidden=true;
      } catch(error) {if(error.name!=='AbortError'){status.textContent=root.dataset.loadError;failed=true;}} finally {loading=false;}
    };
    const observer=new IntersectionObserver(entries=>{if(entries.some(e=>e.isIntersecting))load();},{rootMargin:'250px'});
    function observe(){observer.disconnect();const next=root.querySelector('[data-dl-next]');if(next&&root.dataset.pagination==='infinite_scroll')observer.observe(next);}
    root.addEventListener('click',e=>{const next=e.target.closest('[data-dl-next]');if(next&&root.dataset.pagination==='infinite_scroll'&&!failed){e.preventDefault();load();}},{signal:abort.signal});
    observe();return()=>{abort.abort();observer.disconnect();};
  }

  function lookbook(root) {
    const dialog = root.querySelector('[data-dl-look-dialog]');
    if (!dialog) return () => {};
    const abort = new AbortController();
    const on = (el, type, fn) => el.addEventListener(type, fn, { signal: abort.signal });
    const looks = [...root.querySelectorAll('[data-dl-look]')];
    const image = dialog.querySelector('[data-dl-look-image]');
    const stage = dialog.querySelector('[data-dl-look-stage]');
    const zoom = dialog.querySelector('[data-dl-look-zoom]');
    const previous = dialog.querySelector('[data-dl-look-prev]');
    const next = dialog.querySelector('[data-dl-look-next]');
    let index = 0, previousOverflow, opener, locked = false;
    const setZoom = enlarged => {
      dialog.classList.toggle('is-enlarged', enlarged);
      zoom.setAttribute('aria-pressed', String(enlarged));
      stage.scrollTop = 0; stage.scrollLeft = 0;
    };
    const show = target => {
      index = Math.max(0, Math.min(looks.length - 1, target));
      const look = looks[index];
      image.src = look.href;
      image.alt = look.querySelector('img').alt;
      dialog.querySelector('[data-dl-look-position]').textContent = `${look.dataset.label} / ${String(looks.length).padStart(2, '0')}`;
      previous.disabled = index === 0;
      next.disabled = index === looks.length - 1;
      setZoom(false);
    };
    const open = look => {
      closeMenu(false);
      document.dispatchEvent(new CustomEvent('dl:overlay-open'));
      opener = look;
      show(looks.indexOf(look));
      if (!dialog.open) {
        previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        locked = true;
        dialog.showModal();
      }
    };
    const release = restore => {
      if (!locked) return;
      locked = false;
      document.documentElement.style.overflow = previousOverflow;
      setZoom(false);
      if (restore && opener?.isConnected) opener.focus({ preventScroll: true });
    };
    const close = (restore = true) => { dialog.close(); release(restore); };
    on(root, 'click', e => {
      const look = e.target.closest('[data-dl-look]');
      if (!look || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault(); open(look);
    });
    on(previous, 'click', () => show(index - 1));
    on(next, 'click', () => show(index + 1));
    on(zoom, 'click', () => setZoom(!dialog.classList.contains('is-enlarged')));
    on(dialog.querySelector('[data-dl-look-close]'), 'click', () => close());
    on(dialog, 'cancel', e => {
      if (dialog.classList.contains('is-enlarged')) { e.preventDefault(); setZoom(false); }
    });
    on(dialog, 'close', () => { if (!dialog.open) release(true); });
    on(document, 'dl:overlay-open', () => { if (dialog.open) close(false); });
    on(document, 'dl:cart-opening', () => { if (dialog.open) close(false); });
    on(dialog, 'keydown', e => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault(); show(index + (e.key === 'ArrowRight' ? 1 : -1));
      }
    });
    on(document, 'shopify:block:select', e => {
      const look = looks.find(el => el.contains(e.target) || el.dataset.blockId === e.detail.blockId);
      if (look) open(look);
    });
    return () => {
      if (dialog.open) close(false);
      abort.abort();
    };
  }

  function wordmark(el) {
    let frame;
    const fit=()=>{cancelAnimationFrame(frame);frame=requestAnimationFrame(()=>{
      el.style.fontSize='';
      if(innerWidth<=600)return;
      const width=el.clientWidth,range=document.createRange();range.selectNodeContents(el);
      const textWidth=range.getBoundingClientRect().width;
      if(textWidth>0)el.style.fontSize=`${Math.min(300,parseFloat(getComputedStyle(el).fontSize)*width/textWidth)}px`;
    });};
    const observer=new ResizeObserver(fit);observer.observe(el.parentElement);document.fonts.ready.then(()=>{if(el.isConnected)fit();});fit();
    return()=>{observer.disconnect();cancelAnimationFrame(frame);};
  }
  function init(scope=document){
    for(const [el,cleanup] of controllers){if(!el.isConnected){cleanup();controllers.delete(el);}}
    for(const [selector,setup] of [['[data-dl-header]',header],['[data-dl-product]',product],['[data-dl-collection]',collection],['[data-dl-lookbook]',lookbook],['[data-dl-wordmark]',wordmark]]){
      const elements=[...(scope.matches?.(selector)?[scope]:[]),...scope.querySelectorAll(selector)];
      elements.forEach(el=>{if(!controllers.has(el))controllers.set(el,setup(el));});
    }
  }
  document.addEventListener('cart:change',e=>{if(Number.isFinite(e.detail?.item_count))document.querySelectorAll('[data-dl-cart-count]').forEach(el=>el.textContent=e.detail.item_count);});
  document.addEventListener('shopify:section:load',e=>init(e.target));
  document.addEventListener('shopify:section:unload',e=>{for(const[el,cleanup]of controllers){if(e.target.contains(el)){cleanup();controllers.delete(el);}}});
  window.addEventListener('pageshow',()=>init());
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',()=>init(),{once:true});else init();
})();
